import { computed, ref, watch } from 'vue'
import type { Ref } from 'vue'

import type { SelectItem, SelectRawItem, SelectSearchable, SelectValue } from './select.types'

const normalizeText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^\w\s]/gi, '')
    .toLowerCase()

export const matchesText = (haystack: string, needle: string) =>
  normalizeText(haystack).includes(normalizeText(needle.trim()))

export interface UseSelectOptions {
  items: () => SelectRawItem[]
  model: Ref<SelectValue | SelectValue[] | undefined>
  multiple: () => boolean
  searchable: () => SelectSearchable
  searchThreshold: () => number
  searchableFields: () => string[] | undefined
  selectedOnTop: () => boolean
  limit: () => number | undefined
  valueKey: () => string
  labelKey: () => string
  descriptionKey: () => string
}

export const useSelect = (options: UseSelectOptions) => {
  const search = ref('')

  const matchesSearch = (item: SelectItem): boolean => {
    const fields = options.searchableFields()
    if (fields?.length) {
      const record = item.raw as Record<string, unknown>
      return fields.some((field) => matchesText(String(record?.[field] ?? ''), search.value))
    }
    return matchesText(item.label, search.value) || matchesText(item.description ?? '', search.value)
  }

  const normalizeItem = (raw: SelectRawItem, index: number): SelectItem | null => {
    if (raw === null || raw === undefined) return null

    if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
      return { label: String(raw), value: raw, disabled: false, type: 'item', raw, index }
    }

    if (Array.isArray(raw)) {
      return { label: String(raw[1] ?? raw[0] ?? ''), value: raw[0] ?? null, disabled: false, type: 'item', raw, index }
    }

    const record = raw as Record<string, unknown>
    const label = record[options.labelKey()] ?? record.value ?? ''
    const value = (record[options.valueKey()] ?? record.value ?? label) as SelectValue

    return {
      label: String(label ?? ''),
      value,
      description: options.descriptionKey()
        ? (record[options.descriptionKey()] as string | undefined)
        : (record.description as string | undefined),
      icon: record.icon as string | undefined,
      disabled: record.disabled === true,
      type: (record.type as SelectItem['type']) ?? 'item',
      raw,
      index
    }
  }

  const normalizedItems = computed<SelectItem[]>(() => {
    return options.items()
      .map((raw, index) => normalizeItem(raw, index))
      .filter((item): item is SelectItem => item !== null)
  })

  const selectableItems = computed(() => normalizedItems.value.filter((item) => item.type === 'item'))

  const isSearchable = computed(() => {
    const mode = options.searchable()
    if (mode === true || mode === 'always') return true
    if (mode === false || mode === 'never') return false
    return normalizedItems.value.length > options.searchThreshold()
  })

  const selectedValues = computed<SelectValue[]>(() => {
    const value = options.model.value
    if (options.multiple()) return Array.isArray(value) ? value : []
    if (Array.isArray(value)) return value
    return value === undefined || value === null || value === '' ? [] : [value]
  })

  const isSelected = (value: SelectValue) => selectedValues.value.includes(value)

  const selectedItems = computed<SelectItem[]>(() => {
    return selectedValues.value.map((value) => {
      const found = normalizedItems.value.find((item) => item.value === value)
      if (found) return found
      return {
        label: String(value),
        value,
        disabled: false,
        type: 'item' as const,
        raw: value as SelectRawItem,
        index: -1
      }
    })
  })

  const visibleItems = computed<SelectItem[]>(() => {
    let list = normalizedItems.value

    if (search.value && isSearchable.value) {
      list = list.filter((item) => item.type === 'item' && matchesSearch(item))
    }

    if (options.selectedOnTop() && options.multiple() && !search.value) {
      const selected: SelectItem[] = []
      const rest: SelectItem[] = []
      for (const item of list) {
        (isSelected(item.value) ? selected : rest).push(item)
      }
      list = [...selected, ...rest]
    }

    return list
  })

  const isLimitReached = computed(() => {
    const limit = options.limit()
    return options.multiple() && limit != null && selectedValues.value.length >= limit
  })

  const activeIndex = ref(-1)

  const activeItem = computed<SelectItem | null>(() => visibleItems.value[activeIndex.value] ?? null)

  const isSelectable = (item?: SelectItem) => !!item && item.type === 'item' && !item.disabled

  const findSelectableIndex = (from: number, direction: 1 | -1) => {
    const list = visibleItems.value
    let index = from
    while (index >= 0 && index < list.length) {
      if (isSelectable(list[index])) return index
      index += direction
    }
    return -1
  }

  const moveActive = (delta: 1 | -1) => {
    const list = visibleItems.value
    const count = list.length
    if (!count) return
    let index = activeIndex.value < 0 ? (delta === 1 ? -1 : count) : activeIndex.value
    for (let step = 0; step < count; step += 1) {
      index = (index + delta + count) % count
      if (isSelectable(list[index])) {
        activeIndex.value = index
        return
      }
    }
  }

  const setActive = (index: number) => {
    activeIndex.value = index
  }

  const setActiveByValue = (value: SelectValue) => {
    const index = visibleItems.value.findIndex((item) => item.value === value)
    if (index >= 0) activeIndex.value = index
  }

  const typeAhead = (char: string) => {
    const query = char.toLowerCase()
    const list = visibleItems.value
    const start = activeIndex.value + 1
    for (let i = 0; i < list.length; i += 1) {
      const item = list[(start + i) % list.length]
      if (item.type === 'item' && item.label.toLowerCase().startsWith(query)) {
        activeIndex.value = (start + i) % list.length
        return
      }
    }
  }

  const toggleItem = (item: SelectItem): SelectItem | null => {
    if (item.disabled || item.type !== 'item') return null

    if (options.multiple()) {
      const current = selectedValues.value
      if (current.includes(item.value)) {
        options.model.value = current.filter((value) => value !== item.value)
        return item
      }
      const limit = options.limit()
      if (limit != null && current.length >= limit) return null
      options.model.value = [...current, item.value]
      return item
    }

    options.model.value = item.value
    return item
  }

  const clear = () => {
    options.model.value = options.multiple() ? [] : null
  }

  const removeValue = (value: SelectValue) => {
    if (options.multiple()) {
      options.model.value = selectedValues.value.filter((item) => item !== value)
    } else if (isSelected(value)) {
      options.model.value = null
    }
  }

  const toggleAll = () => {
    if (!options.multiple()) return
    const allSelected = selectableItems.value.length > 0 && selectableItems.value.every((item) => isSelected(item.value))
    options.model.value = allSelected ? [] : selectableItems.value.filter((item) => !item.disabled).map((item) => item.value)
  }

  const allSelected = computed(
    () => selectableItems.value.length > 0 && selectableItems.value.every((item) => isSelected(item.value))
  )

  const someSelected = computed(
    () => !allSelected.value && selectableItems.value.some((item) => isSelected(item.value))
  )

  watch(visibleItems, (list) => {
    const current = list[activeIndex.value]
    if (!isSelectable(current)) {
      activeIndex.value = findSelectableIndex(0, 1)
    }
  })

  return {
    search,
    normalizedItems,
    selectableItems,
    selectedValues,
    selectedItems,
    visibleItems,
    isSearchable,
    isLimitReached,
    allSelected,
    someSelected,
    activeIndex,
    activeItem,
    isSelectable,
    isSelected,
    moveActive,
    setActive,
    setActiveByValue,
    typeAhead,
    toggleItem,
    toggleAll,
    clear,
    removeValue
  }
}
